/**
 * StockAdjustModal — add or remove stock for a single inventory item.
 * Inserts a signed stock_movements row and updates inventory_items.quantity_on_hand
 * via adjustStock from @gym/api-client.
 */
import { adjustStock } from '@gym/api-client';
import type { StockMovementType } from '@gym/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { Field, Input, Select } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

export interface StockAdjustModalProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  productId: string;
  inventoryItemId: string;
  /** Shown for context; the atomic RPC re-reads the authoritative on-hand qty. */
  currentQty: number;
}

type Direction = 'add' | 'remove';

const ADD_TYPES: StockMovementType[] = ['purchase_in', 'adjustment'];
const REMOVE_TYPES: StockMovementType[] = ['wastage', 'adjustment'];

export function StockAdjustModal({
  open,
  onOpenChange,
  productId,
  inventoryItemId,
  currentQty,
}: StockAdjustModalProps): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const qc = useQueryClient();

  const [direction, setDirection] = useState<Direction>('add');
  const [qty, setQty] = useState('1');
  const [movementType, setMovementType] = useState<StockMovementType>('purchase_in');
  const [reason, setReason] = useState('');

  function handleDirectionChange(d: Direction) {
    setDirection(d);
    setMovementType(d === 'add' ? 'purchase_in' : 'wastage');
  }

  const staffId = claims?.staff_id ?? '';
  const claimsReady = !!staffId && !!claims?.tenant_id;

  const mutation = useMutation({
    mutationFn: () => {
      const parsedQty = Math.max(1, Math.round(Number(qty)));
      const delta = direction === 'add' ? parsedQty : -parsedQty;
      const trimmedReason = reason.trim();
      return adjustStock(supabase, {
        inventoryItemId,
        delta,
        movementType,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
        staffId,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['low_stock'] });
      void qc.invalidateQueries({ queryKey: ['inventory_qty'] });
      void qc.invalidateQueries({ queryKey: ['product_inventory', productId] });
      toast.success(t('retail.adjustStock.title'));
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(t('retail.adjustStock.title'), msg);
    },
  });

  const types = direction === 'add' ? ADD_TYPES : REMOVE_TYPES;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!mutation.isPending) onOpenChange(o);
      }}
      title={t('retail.adjustStock.title')}
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!claimsReady}
            onClick={() => mutation.mutate()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!claimsReady ? (
          <p className="rounded-lg border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
            {t('common.error')}
          </p>
        ) : null}

        {/* Direction toggle */}
        <div className="flex gap-2">
          <Button
            variant={direction === 'add' ? 'primary' : 'secondary'}
            className="flex-1"
            onClick={() => handleDirectionChange('add')}
            type="button"
          >
            {t('retail.adjustStock.add')}
          </Button>
          <Button
            variant={direction === 'remove' ? 'destructive' : 'secondary'}
            className="flex-1"
            onClick={() => handleDirectionChange('remove')}
            type="button"
          >
            {t('retail.adjustStock.remove')}
          </Button>
        </div>

        {/* Current on-hand (context only) */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted">{t('retail.stock')}</span>
          <span className="tnum font-medium text-fg">{currentQty}</span>
        </div>

        {/* Quantity */}
        <Field label={t('retail.adjustStock.quantity')} required>
          {(id) => (
            <Input
              id={id}
              type="number"
              min={1}
              step={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          )}
        </Field>

        {/* Movement type */}
        <Field label={t('retail.adjustStock.typeLabel')}>
          {(id) => (
            <Select
              id={id}
              value={movementType}
              onChange={(e) => setMovementType(e.target.value as StockMovementType)}
            >
              {types.map((v) => (
                <option key={v} value={v}>
                  {t(`retail.adjustStock.type.${v}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* Reason */}
        <Field label={t('retail.adjustStock.reason')}>
          {(id) => (
            <Input id={id} type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
        </Field>
      </div>
    </Modal>
  );
}
